/**
 * Lo stato della rete, quando `navigator.onLine` non basta.
 *
 * ## Perché non basta
 *
 * `navigator.onLine` risponde a una domanda più modesta di quella che sembra: dice se esiste
 * un'interfaccia di rete attiva, non se si arriva da qualche parte. In una WebView agganciata al
 * Wi-Fi di un cantiere — quello col portale che chiede la password e non l'ha mai avuta — resta
 * `true` per tutto il tempo. La coda delle timbrature lo interroga per decidere se vale la pena
 * ritentare (`attendanceQueue.offline()`): con quel `true` ritenta, fallisce, e consuma i cinque
 * tentativi che aveva per il momento in cui la rete torna davvero.
 *
 * un plugin di rete legge lo stato dal sistema operativo, che il portale l'ha visto, e manda un
 * evento quando cambia — anche quando l'app è tornata in primo piano dopo ore, dove l'evento
 * `online` del browser non arriva mai perché nel frattempo la pagina non era viva.
 *
 * ## Come si comporta
 *
 * Lo stato nativo viene tenuto in una variabile aggiornata dal listener, perché chi chiama
 * `offline()` lo fa in mezzo a una decisione sincrona e non può aspettare una promessa. Finché il
 * plugin non ha risposto — e sul web, sempre — si ricade su `navigator.onLine`: peggio di così non
 * si sta, ed è esattamente il comportamento di prima.
 */
import { nativeRuntime } from '@/shared/lib/nativePlatform';
import { env } from '@/shared/lib/env';

/** `null` = non lo sappiamo ancora dal sistema; si usa il ripiego del browser. */

/** Il ripiego di sempre: vero solo se il browser è **certo** di essere scollegato. */
function offlineAccordingToBrowser(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/* ───────────────────────────── la sonda ───────────────────────────── */

/**
 * LA DOMANDA CHE NESSUN PLUGIN SA.
 *
 * `navigator.onLine` dice «c'e' un'interfaccia». Un plugin di rete dice «il sistema ha
 * validato la connessione». Nessuno dei due dice **«il nostro server risponde»**, che e' la sola
 * cosa che conta per decidere se svuotare la coda delle timbrature — perche' e' una proprieta'
 * della rete **fra il telefono e noi**, non del telefono.
 *
 * ⚠️ Il caso che la fa esistere e' il Wi-Fi col portale: quello del cantiere, dell'albergo,
 * dell'aeroporto. Li' l'interfaccia c'e', il sistema la valida (il portale **risponde**), e
 * l'evento `online` del browser scatta. La coda ci crede, ritenta, fallisce, e **consuma i
 * cinque tentativi** che aveva per il momento in cui la rete torna davvero.
 *
 * ⚠️ E il plugin Cordova non lo chiude: `cordova-plugin-network-information` legge
 * `getActiveNetworkInfo().isConnected()` e il tipo di interfaccia — su un portale risponde
 * `true` e `wifi`, cioe' lo stesso falso positivo di `navigator.onLine`. Verificato leggendo il
 * suo `NetworkManager.java`. Forkarlo per aggiungere `NET_CAPABILITY_VALIDATED` chiuderebbe il
 * caso **solo su Android** (su iOS quel flag non esiste) e su un'API deprecata da Android 10:
 * due meccanismi da mantenere invece di uno.
 *
 * ## Come riconosce il portale
 *
 * ⚠️ `res.ok` **non basta**, ed e' il punto delicato: un portale risponde `200` con la propria
 * pagina di accesso. La sonda chiede quindi un indirizzo **nostro e autenticato**, e accetta
 * qualunque risposta che il *nostro* server possa aver dato — un `401` va benissimo, perche'
 * per rispondere `401` bisogna esserci. Quel che rifiuta e' l'HTML: il portale non conosce quel
 * percorso e manda la sua pagina, che e' `text/html`.
 *
 * In altre parole: non si guarda **se** la risposta e' buona, si guarda **chi** ha risposto.
 */
/**
 * ⚠️ Un percorso che **esiste**, e non e' un dettaglio. Era `/api/account`, che il server non
 * conosce: rispondeva `404`. Per il criterio della sonda un `404` nostro vale — non e' HTML,
 * quindi qualcuno di nostro ha risposto — ma la cosa regge solo finche' il `404` non e' una
 * *pagina*. Il giorno che davanti all'API ci fosse qualcosa che serve un 404 in `text/html`, la
 * sonda direbbe «portale» e l'app si dichiarerebbe offline con la rete perfetta.
 *
 * `/api/account/me` e' autenticato e chiesto **senza credenziali**: risponde `401`, che e' la
 * risposta migliore possibile qui — per darla bisogna esserci.
 *
 * E c'e' il costo di chi guarda: due `404` rossi nell'inspector a ogni ritorno in primo piano
 * fanno perdere tempo a chiunque apra quel pannello cercando un altro difetto.
 */
const PROBE_PATH = '/api/account/me';

/** Quanto vale una risposta della sonda. Oltre, si torna a chiedere. */
export const PROBE_FRESHNESS_MS = 15_000;

/** Il tempo massimo di attesa. Corto di proposito: qui il pregio e' fallire presto. */
const PROBE_TIMEOUT_MS = 4_000;

let probeOutcome: boolean | null = null;
let whenToProbe = 0;

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

/** La risposta della sonda, se abbastanza recente. `null` = non lo sappiamo. */
function freshProbe(): boolean | null {
  if (probeOutcome === null) return null;
  return now() - whenToProbe < PROBE_FRESHNESS_MS ? probeOutcome : null;
}

/**
 * Il nostro server risponde?
 *
 * Non solleva mai: un fallimento **e'** la risposta. Aggiorna lo stato che `offline()` legge,
 * cosi' la prossima decisione sincrona sa quel che questa chiamata ha scoperto.
 */
async function runProbe(): Promise<boolean> {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const deadline = controller ? setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS) : null;
  try {
    const response = await fetch(`${env.VITE_API_BASE_URL}${PROBE_PATH}`, {
      method: 'HEAD',
      cache: 'no-store',
      /* Nessuna credenziale: la sonda non deve dipendere dall'avere un token valido, e un `401`
         e' una risposta buona per il suo scopo. */
      signal: controller?.signal,
    });
    /* Un `HEAD` su un `401` puo' non avere `content-type`: assente vuol dire «non e' una pagina»,
       quindi conta come nostro. E' il portale che si tradisce mandando dell'HTML. */
    const tipo = response.headers.get('content-type') ?? '';
    probeOutcome = !tipo.toLowerCase().includes('text/html');
  } catch {
    /* Timeout, DNS, rete assente: in tutti e tre i casi non arriviamo. */
    probeOutcome = false;
  } finally {
    if (deadline !== null) clearTimeout(deadline);
    whenToProbe = now();
  }
  return probeOutcome;
}

/**
 * Una sonda per volta.
 *
 * ⚠️ Al ritorno in primo piano partivano **due** chiamate, e la cache di freschezza non le
 * fermava: `visibilitychange` e il `resume` di Cordova arrivano insieme, quindi entrambe partono
 * *prima* che una delle due abbia registrato un esito — e la freschezza si guarda all'inizio, non
 * alla fine. Non e' un difetto solo estetico: sono due richieste di rete a ogni rientro
 * dell'applicazione, su una connessione che la sonda esiste proprio per non dare per buona.
 *
 * Chi chiede mentre una e' in volo riceve **quella**: e' la risposta che sta arrivando.
 */
let probeInFlight: Promise<boolean> | null = null;

export function probe(): Promise<boolean> {
  if (probeInFlight) return probeInFlight;
  const current = runProbe();
  probeInFlight = current;
  void current.finally(() => {
    if (probeInFlight === current) probeInFlight = null;
  });
  return current;
}

/**
 * Siamo offline adesso?
 *
 * Sincrona apposta: la usa chi sta decidendo se tentare una POST, e trasformarla in una promessa
 * obbligherebbe a rendere asincrone tre funzioni che oggi non lo sono.
 *
 * ⚠️ L'ordine delle due fonti non e' casuale, va dalla piu' informata alla piu' modesta: la
 * **sonda** sa se arriviamo, il **browser** sa solo se c'e' un'interfaccia. La sonda conta solo se
 * e' recente: una risposta di un minuto fa non dice niente su adesso, e crederci sarebbe peggio
 * che non averla.
 *
 * ⚠️ Erano **tre**, e il gradino di mezzo — «il sistema dice che la connessione e' validata» — e'
 * sparito col plugin di rete del guscio precedente, il 09-09-2026. In quello Cordova non c'e' un plugin di
 * rete installato, e vale sapere cosa si e' perso e quanto: quel gradino si consultava **solo**
 * quando la sonda era scaduta, cioe' nella finestra in cui non sapevamo niente di recente. Il
 * caso per cui questo file esiste — il portale che risponde `200` con la propria pagina di
 * accesso — lo decide la sonda, e quello e' intatto.
 *
 * Se un domani si volesse indietro quel gradino, il pezzo e'
 * `cordova-plugin-network-information` (`navigator.connection.type` e gli eventi `online`/
 * `offline` sul `document`): e' una riga di plugin e una funzione, non un progetto.
 */
export function offline(): boolean {
  const fromProbe = freshProbe();
  if (fromProbe !== null) return fromProbe === false;
  return offlineAccordingToBrowser();
}

/**
 * Chiama `cb` quando la rete **torna**. Restituisce la disiscrizione.
 *
 * ⚠️ Era **una coppia** di strade — l'evento `online` del browser e il listener nativo di
 * il listener nativo del guscio precedente — e ora resta la prima. Il filtro che conta non e' cambiato: la richiamata
 * parte solo se la sonda dice che il **nostro** server risponde, quindi un `online` che arriva
 * agganciandosi al portale non svuota piu' la coda contro una pagina di accesso.
 */
export function onOnlineReturn(cb: () => void): () => void {
  /*
   * ⚠️ La sonda **prima** della richiamata, ed e' il punto per cui esiste.
   *
   * L'evento `online` del browser scatta quando ci si aggancia al Wi-Fi col portale: senza
   * questo filtro la coda si svuotava li', bruciando i suoi cinque tentativi contro una pagina
   * di accesso. Ora la richiamata parte solo se il **nostro** server ha risposto.
   *
   * ⚠️ Chiamare due volte lo svuotamento non fa danno (la coda e' idempotente, ogni riga ha il
   * suo id); **non** chiamarlo lascia una timbratura ferma sul telefono. Per questo in caso di
   * dubbio si chiama: la sonda che fallisce ferma il giro, la sonda che non parte no.
   */
  const ifWeArrive = () => { void probe().then((ok) => { if (ok) cb(); }); };
  const daBrowser = ifWeArrive;
  if (typeof window !== 'undefined') window.addEventListener('online', daBrowser);

  return () => {
    if (typeof window !== 'undefined') window.removeEventListener('online', daBrowser);
  };
}

/**
 * Sonda quando l'app torna in primo piano.
 *
 * ⚠️ E' il caso che nessun evento di rete copre: il telefono e' stato in tasca per ore, la rete
 * e' cambiata tre volte, e la pagina non era viva per sentire `online`. Al ritorno non sappiamo
 * niente — e la coda potrebbe avere timbrature da mandare.
 *
 * Le tre strade coesistono perche' i tre gusci annunciano il ritorno in modo diverso:
 * `document.visibilitychange` sul web, l'evento `resume` in Cordova, e il `resume` di
 * dal guscio nativo. Nessuna esclude l'altra: sondare due volte costa una richiesta `HEAD`.
 */
export function probeOnReturn(): () => void {
  const done: Array<() => void> = [];

  if (typeof document !== 'undefined') {
    const onVisible = () => { if (document.visibilityState === 'visible') void probe(); };
    document.addEventListener('visibilitychange', onVisible);
    done.push(() => document.removeEventListener('visibilitychange', onVisible));
  }

  if (nativeRuntime() === 'cordova' && typeof document !== 'undefined') {
    const alResume = () => { void probe(); };
    document.addEventListener('resume', alResume, false);
    done.push(() => document.removeEventListener('resume', alResume, false));
  }

  return () => { done.forEach((f) => f()); };
}

/** Solo per i test: rimette la sonda a «non lo sappiamo». */
export function resetNetworkStatus(): void {
  probeOutcome = null;
  whenToProbe = 0;
}
